import { useCallback, useEffect, useRef, useState } from "react";

import i18next from "i18next";
import { IoAddOutline, IoLogOutOutline } from "react-icons/io5";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useAuth } from "../../../context/auth";
import { useSupport } from "../../../context/support";
import { clearSupportGuestSession, supportTickets } from "../../../protocols/rest/support";
import { isSupportAccessError, supportErrorText } from "../utils";

import { ContainerComponent, DotsLoadingComponent } from "../../ui";
import SupportAccessForm from "../SupportAccessForm";
import SupportTicketCreateForm from "../SupportTicketCreateForm";
import SupportTicketList from "../SupportTicketList";

import {
  Buttons,
  FilterTab,
  FilterTabs,
  FormError,
  HeaderActions,
  Loading,
  PageHeader,
  PageSubtitle,
  PageTitle,
  PageWrapper,
  PrimaryButton,
  SecondaryButton,
} from "../style";

const PER_PAGE = 20;

/**
 * /support — заявки клиента.
 * Залогиненный: свои заявки + создание (можно выбрать заказ). Гость с подтверждённым доступом: заявки его заказа.
 * Гость без доступа: форма «Найти мой заказ» (номер + email → код из письма).
 * Параметры: ?order_id= (кнопки на заказе / в списке заказов) — сразу открывает создание по этому заказу; ?email= — для гостя.
 */
const SupportCenter = () => {
  const { t } = i18next;
  const navigate = useNavigate();
  const { isAuth } = useAuth();
  const { mode, guest, meta, revision } = useSupport();
  const [searchParams, setSearchParams] = useSearchParams();

  const presetOrderId = searchParams.get("order_id") || "";
  const presetEmail = searchParams.get("email") || "";

  const [filter, setFilter] = useState("open");
  const [tickets, setTickets] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [listError, setListError] = useState(null);
  const [showCreate, setShowCreate] = useState(!!presetOrderId);

  // гостевой доступ истёк — предзаполняем форму доступа номером того же заказа
  const lastGuestOrderRef = useRef(guest?.order_id || "");
  if (guest?.order_id) lastGuestOrderRef.current = guest.order_id;

  // ссылка «написать по заказу» на другой заказ, чем подтверждённый гостевой доступ, — подтверждаем новый
  const needsOtherOrderAccess =
    mode === "guest" && presetOrderId && String(presetOrderId) !== String(guest?.order_id || "");

  const loadedCountRef = useRef(PER_PAGE);

  const loadTickets = useCallback(
    async ({ page = 1, append = false, perPage = PER_PAGE } = {}) => {
      if (!mode || needsOtherOrderAccess) return;

      append ? setIsLoadingMore(true) : setIsLoading(true);
      setListError(null);

      try {
        const params = { page, per_page: perPage };
        if (filter === "open") params.status = "open";

        const data = await supportTickets(params);
        const list = Array.isArray(data?.data) ? data.data : [];

        setTickets(current => {
          const next = append ? [...current, ...list.filter(item => !current.some(ticket => ticket.id === item.id))] : list;
          loadedCountRef.current = Math.max(PER_PAGE, Math.ceil(next.length / PER_PAGE) * PER_PAGE);
          return next;
        });
        setPagination(data?.meta || null);
      } catch (error) {
        if (isSupportAccessError(error) && mode === "user") {
          navigate(`/auth?redirect=${encodeURIComponent("/support")}`);
          return;
        }

        setListError(supportErrorText(error));
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [mode, filter, needsOtherOrderAccess, navigate]
  );

  // смена режима / фильтра — с первой страницы
  useEffect(() => {
    setTickets([]);
    setPagination(null);
    loadTickets();
  }, [loadTickets, guest?.token]);

  // событие realtime / изменения по опросу — тихо перечитываем уже загруженное
  const isFirstRevision = useRef(true);
  useEffect(() => {
    if (isFirstRevision.current) {
      isFirstRevision.current = false;
      return;
    }

    loadTickets({ perPage: Math.min(loadedCountRef.current, 100) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revision]);

  const closeCreate = () => {
    setShowCreate(false);

    if (searchParams.has("order_id")) {
      searchParams.delete("order_id");
      searchParams.delete("email");
      setSearchParams(searchParams, { replace: true });
    }
  };

  const handleCreated = ticket => {
    if (ticket?.id) {
      navigate(`/support/${ticket.id}`);
    } else {
      closeCreate();
      loadTickets();
    }
  };

  if (!mode || needsOtherOrderAccess) {
    return (
      <PageWrapper>
        <ContainerComponent>
          <SupportAccessForm
            key={presetOrderId || lastGuestOrderRef.current}
            presetOrderId={presetOrderId || lastGuestOrderRef.current}
            presetEmail={presetEmail}
          />
        </ContainerComponent>
      </PageWrapper>
    );
  }

  const isGuest = mode === "guest";
  // догрузка считается по количеству уже показанных (после тихого перечитывания per_page бывает больше)
  const hasMore = !!pagination && Number(pagination.total) > tickets.length;

  return (
    <PageWrapper>
      <ContainerComponent>
        <PageHeader>
          <div>
            <PageTitle>{isGuest ? t("support_guest_order", { order: guest?.order_id }) : t("support_title")}</PageTitle>
            <PageSubtitle>{t("support_subtitle")}</PageSubtitle>
          </div>

          <HeaderActions>
            {!showCreate && (
              <PrimaryButton type="button" onClick={() => setShowCreate(true)}>
                <IoAddOutline />
                {t("support_new_ticket")}
              </PrimaryButton>
            )}
            {isGuest && !isAuth && (
              <SecondaryButton type="button" onClick={clearSupportGuestSession}>
                <IoLogOutOutline />
                {t("support_guest_exit")}
              </SecondaryButton>
            )}
          </HeaderActions>
        </PageHeader>

        {showCreate && (
          <div style={{ marginBottom: 20 }}>
            <SupportTicketCreateForm
              meta={meta}
              isGuest={isGuest}
              guestOrderId={guest?.order_id}
              presetOrderId={isGuest ? "" : presetOrderId}
              onCreated={handleCreated}
              onCancel={closeCreate}
            />
          </div>
        )}

        <FilterTabs>
          <FilterTab type="button" $active={filter === "open"} onClick={() => setFilter("open")}>
            {t("support_filter_open")}
          </FilterTab>
          <FilterTab type="button" $active={filter === "all"} onClick={() => setFilter("all")}>
            {t("support_filter_all")}
          </FilterTab>
        </FilterTabs>

        {listError && (
          <div style={{ marginTop: 15 }}>
            <FormError>{listError}</FormError>
          </div>
        )}

        {isLoading && !tickets.length ? (
          <Loading>
            <DotsLoadingComponent />
          </Loading>
        ) : (
          !listError && (
            <SupportTicketList
              tickets={tickets}
              emptyText={filter === "open" ? t("support_no_open_tickets") : t("support_no_tickets")}
            />
          )
        )}

        {hasMore && (
          <Buttons style={{ justifyContent: "center", marginTop: 15 }}>
            <SecondaryButton
              type="button"
              disabled={isLoadingMore}
              onClick={() => loadTickets({ page: Math.floor(tickets.length / PER_PAGE) + 1, append: true })}
            >
              {t("support_load_more")}
            </SecondaryButton>
          </Buttons>
        )}
      </ContainerComponent>
    </PageWrapper>
  );
};

export default SupportCenter;
